import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdEmail, MdError, MdCheckCircle, MdArrowBack, MdCode } from 'react-icons/md';
import { cn } from '../lib/utils';
import mapBackground from '../assets/proper_map_bg.png';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const SHIP_SPAN_LOGO_DARK = '/ship-span-logo-dark.png';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, portalType: 'tech' }),
      });
      const data = await response.json();
      if (response.ok && data.success) { setSuccess(true); }
      else { setError(data.error || 'Failed to send reset email. Please try again.'); }
    } catch { setError('Network error. Please check your connection and try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="h-screen flex flex-col bg-[#060d18] font-sans text-white overflow-hidden">
      <header className="relative z-30 flex justify-between items-center px-6 py-3 border-b border-white/5 backdrop-blur-md bg-[#060d18]/60 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
            <img src={SHIP_SPAN_LOGO_DARK} alt="Ship Span" className="w-full h-full object-contain" />
          </div>
          <div className="flex flex-col">
            <h1 className="font-bold text-sm leading-tight tracking-wide text-white">Ship Span ERP</h1>
            <p className="text-[9px] uppercase tracking-[0.2em] text-white/50">Maritime Solutions</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-white/70">
            <MdCode className="text-white/50" />
            <span className="font-medium">Tech Portal</span>
          </div>
          <button onClick={() => navigate('/login')} className="bg-[#0088cc] hover:bg-[#0077b3] text-white px-4 py-1.5 rounded-lg font-semibold text-xs transition-all">SIGN IN</button>
        </div>
      </header>

      <main className="relative z-20 flex-1 flex overflow-hidden m-3 rounded-2xl border border-white/[0.06]">
        <div className="hidden lg:flex lg:w-[55%] relative bg-[#0a1929] overflow-hidden">
          <img src={mapBackground} alt="Maritime Map" className="absolute inset-0 w-full h-full object-cover object-center opacity-60 animate-[slowPan_30s_linear_infinite]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#060d18]/70 via-transparent to-[#060d18]/80 z-10" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a1929]/90 via-transparent to-[#0a1929]/40 z-10" />
          <div className="absolute inset-0 z-10 overflow-hidden opacity-20">
            <div className="absolute w-full h-[1px] bg-gradient-to-r from-transparent via-[#0088cc] to-transparent animate-[scanLine_4s_linear_infinite]" />
          </div>
          <div className="relative z-20 flex flex-col justify-end h-full p-10 pb-12">
            <div className="space-y-4">
              <h2 className="text-4xl font-bold text-white leading-tight tracking-tight">
                Recover Your<br /><span className="text-[#0088cc]">Tech Access</span>
              </h2>
              <p className="text-sm text-white/60 leading-relaxed max-w-md">
                Enter your registered email and we'll send you a secure link to reset your password and regain access.
              </p>
              <div className="flex items-center gap-5 pt-3">
                {['Secure Reset', 'Email Verified', 'Instant Access'].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0088cc]" />
                    <span className="text-[11px] text-white/50 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-[45%] flex items-center justify-center p-6 lg:p-10 bg-[#0b1523] relative overflow-y-auto">
          <div className="absolute inset-0 opacity-[0.02] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzBhOGJjZSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] pointer-events-none" />
          <div className="relative z-10 w-full max-w-sm">
            {success ? (
              <div className="space-y-6 text-center">
                <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mx-auto">
                  <MdCheckCircle className="w-8 h-8 text-emerald-400" />
                </div>
                <div className="space-y-3">
                  <h2 className="text-2xl font-bold text-white">Check Your Email</h2>
                  <p className="text-sm text-white/60">A password reset link has been sent to <strong className="text-white">{email}</strong></p>
                  <p className="text-xs text-white/40">The link will expire in 1 hour. Check your spam folder if you don't see it.</p>
                </div>
                <button onClick={() => navigate('/login')} className="w-full py-3 rounded-xl text-sm font-semibold bg-[#0088cc] hover:bg-[#0099e6] text-white shadow-lg shadow-[#0088cc]/20 transition-all active:scale-[0.98]">Back to Login</button>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <div className="mb-5 flex justify-center sm:justify-start">
                    <div className="w-14 h-14 flex items-center justify-center">
                      <img src={SHIP_SPAN_LOGO_DARK} alt="Ship Span" className="w-full h-full object-contain" />
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold text-white tracking-tight">Forgot Password</h2>
                  <p className="text-sm text-white/60 mt-1.5">Enter your email to receive a reset link</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm animate-[shake_0.3s_ease-in-out]">
                      <MdError className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
                    </div>
                  )}
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider">Email Address</label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none"><MdEmail className="text-white/40 text-lg group-focus-within:text-[#0088cc] transition-colors" /></div>
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full pl-11 pr-4 py-3 bg-white/[0.06] border border-white/15 rounded-xl text-sm text-white placeholder:text-white/30 outline-none focus:border-[#0088cc]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#0088cc]/15 transition-all" placeholder="tech@euroasiann.com" />
                    </div>
                  </div>
                  <div className="pt-2 space-y-3">
                    <button type="submit" disabled={loading} className={cn("w-full py-3 rounded-xl text-sm font-semibold transition-all relative overflow-hidden bg-[#0088cc] hover:bg-[#0099e6] text-white shadow-lg shadow-[#0088cc]/20 hover:shadow-[#0088cc]/30 active:scale-[0.98]", loading && "opacity-70 cursor-not-allowed")}>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-[shimmer_2s_infinite] pointer-events-none" />
                      <span className="relative z-10">{loading ? 'Sending...' : 'Send Reset Link'}</span>
                    </button>
                    <button type="button" onClick={() => navigate('/login')} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium text-white/50 hover:text-white/80 hover:bg-white/[0.04] transition-all">
                      <MdArrowBack className="w-4 h-4" />Back to Login
                    </button>
                  </div>
                </form>
              </>
            )}
            <div className="mt-8 pt-5 border-t border-white/[0.06] text-center">
              <p className="text-[11px] text-white/40">Secure access to Ship Span ERP Tech Portal</p>
            </div>
          </div>
        </div>
      </main>

      <footer className="relative z-20 text-center py-2.5 border-t border-white/[0.04] bg-[#060d18]/60 flex-shrink-0">
        <p className="text-[11px] text-white/35">2026 &copy; Ship Span ERP. All Ships Served.</p>
      </footer>

      <style>{`
        @keyframes slowPan { 0% { transform: scale(1.05) translateX(0) translateY(0); } 50% { transform: scale(1.1) translateX(-1%) translateY(-0.5%); } 100% { transform: scale(1.05) translateX(0) translateY(0); } }
        @keyframes scanLine { 0% { top: 0%; } 100% { top: 100%; } }
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-4px); } 75% { transform: translateX(4px); } }
      `}</style>
    </div>
  );
}
